import {
    ResponsiveModal,
    ResponsiveModalBody,
    ResponsiveModalContent,
    ResponsiveModalHeader,
    ResponsiveModalTitle,
    ResponsiveModalTrigger,
} from '@/components/ui/responsive-modal';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/shadcn/dropdown-menu';
import { useIsMobile } from '@/hooks/use-mobile';
import { useTranslations } from '@/hooks/useTranslations';
import { cn } from '@/lib/utils';
import { Loader2 } from 'lucide-react';
import { useState, type KeyboardEvent, type MouseEvent } from 'react';
import type { OptionRow } from '../../types';
import { StatusBadge } from '../calculation-request-badges';

interface InlineCalcStatusCellProps {
    value: string | null | undefined;
    /** Absent means read-only — the same contract the other inline cells use. */
    onUpdate?: (value: string) => Promise<void>;
    statuses: OptionRow[];
    className?: string;
    readOnly?: boolean;
}

/**
 * Inline status editor for the calculation queue.
 *
 * Options come from the admin-managed settings list passed down as a prop, so a status
 * an admin adds shows up here with no code change.
 */
export function InlineCalcStatusCell({ value, onUpdate, statuses, className, readOnly = false }: InlineCalcStatusCellProps) {
    const { t } = useTranslations();
    const isMobile = useIsMobile();
    const [isOpen, setIsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const handleSelect = async (newValue: string) => {
        if (newValue === value || !onUpdate) {
            setIsOpen(false);
            return;
        }

        setIsLoading(true);
        try {
            await onUpdate(newValue);
            setIsOpen(false);
        } catch {
            // The caller surfaces the failure as a toast; keep the cell usable.
        } finally {
            setIsLoading(false);
        }
    };

    // Row click navigates to the detail page, so every trigger has to stop propagation.
    const openSelector = (event?: MouseEvent | KeyboardEvent) => {
        event?.stopPropagation();
        if (!isLoading) {
            setIsOpen(true);
        }
    };

    if (readOnly || !onUpdate || statuses.length === 0) {
        return (
            <div className={cn('flex w-full items-center justify-start px-2 py-1.5', className)}>
                <StatusBadge value={value} options={statuses} />
            </div>
        );
    }

    const trigger = (
        <div
            role="button"
            tabIndex={isLoading ? -1 : 0}
            onClick={openSelector}
            onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    openSelector(event);
                }
            }}
            className={cn('hover:bg-accent flex w-full items-center justify-start rounded px-2 py-1.5 transition-colors', className)}
            aria-disabled={isLoading}
        >
            {isLoading ? <Loader2 className="text-muted-foreground h-4 w-4 animate-spin" /> : <StatusBadge value={value} options={statuses} />}
        </div>
    );

    if (isMobile) {
        return (
            <ResponsiveModal open={isOpen} onOpenChange={setIsOpen} variant="dialog">
                <ResponsiveModalTrigger asChild>{trigger}</ResponsiveModalTrigger>
                <ResponsiveModalContent dialogClassName="sm:max-w-md" onClick={(event) => event.stopPropagation()}>
                    <ResponsiveModalHeader>
                        <ResponsiveModalTitle>{t('calculation_requests.tables.status', 'Status')}</ResponsiveModalTitle>
                    </ResponsiveModalHeader>
                    <ResponsiveModalBody className="flex flex-col gap-2">
                        {statuses.map((option) => (
                            <button
                                key={option.value}
                                type="button"
                                onClick={() => handleSelect(option.value)}
                                className="bg-background hover:bg-accent flex w-full items-center justify-start rounded-md border px-3 py-2 text-left"
                            >
                                <StatusBadge value={option.value} options={statuses} />
                            </button>
                        ))}
                    </ResponsiveModalBody>
                </ResponsiveModalContent>
            </ResponsiveModal>
        );
    }

    return (
        <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
            <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
            <DropdownMenuContent align="start" onClick={(event) => event.stopPropagation()}>
                {statuses.map((option) => (
                    <DropdownMenuItem key={option.value} onClick={() => handleSelect(option.value)} className="cursor-pointer">
                        <StatusBadge value={option.value} options={statuses} />
                    </DropdownMenuItem>
                ))}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
